import { Link, useLocation } from "react-router-dom";
import logo from "../assets/images/avatar1.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown,
  faAngleRight,
  faBars,
  faCaretDown,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState, useRef } from "react";
import constants from "../constants/constants";
import formattingFunctions from "../functions/formatting";
import { motion, AnimatePresence } from "framer-motion";
import MobileHeader from "./mobileHeader.component";
import { useUserParams } from "../hooks/useUserParams";

export default function Header() {
  const { getCurrentLogo } = useUserParams();
  const [menus, setMenus] = useState({
    buying: false,
    selling: false,
    info: false,
    locations: false,
  });
  const [activeMenu, setActiveMenu] = useState(null);
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [headerPosition, setHeaderPosition] = useState(100);
  const [headerTiming, setHeaderTiming] = useState(0.8);
  const location = useLocation();
  const [path, setPath] = useState(location.pathname);
  const [isScrolled, setIsScrolled] = useState(false);

  // Refs
  const headerRef = useRef(null);
  const lastScrollTopRef = useRef(0);

  const onWindowResize = () => {
    setWindowSize(window.innerWidth);
  };

  const menuVariants = {
    hidden: {
      opacity: 0,
      y: -8,
    },
    visible: {
      opacity: 1,
      y: 0,
    },
    transition: {
      duration: 0.35,
      ease: "easeOut",
    }
  };

  useEffect(() => {
    window.addEventListener("resize", onWindowResize);
    return () => {
      window.removeEventListener("resize", onWindowResize);
    };
  }, []);

  const setDropdown = (key, bool) => {
    setMenus((prevMenus) => ({
      ...prevMenus,
      ...Object.fromEntries(
        Object.keys(prevMenus).map((k) => [k, k === key ? bool : false])
      ),
    }));
  };

  const navClick = () => {
    setHeaderPosition(100);
    setHeaderTiming(0);
    setTimeout(() => {
      setHeaderTiming(0.8);
    }, 100);
    setMenus({ buying: false, selling: false, info: false, locations: false });
  };

  useEffect(() => {
    setPath(location.pathname);
  }, [location]);

  const adjustHeader = () => {
    const st = window.scrollY;

    setIsScrolled(st > 0);

    const header = headerRef.current;
    if (!header) return;

    if (st > lastScrollTopRef.current) {
      header.classList.add("hide-header");
    } else {
      header.classList.remove("hide-header");
    }

    lastScrollTopRef.current = st <= 0 ? 0 : st;
  };

  useEffect(() => {
    window.addEventListener("scroll", adjustHeader);
    return () => {
      window.removeEventListener("scroll", adjustHeader);
    };
  }, [location, path]);

  useEffect(() => {
    setIsScrolled(window.scrollY > 0);
  }, [location.pathname]);

  const isHome = location.pathname === "/";

  const hasBackground = !isHome || isScrolled;

  return (
    <>
      {windowSize > 1250 ? (
        <div
          ref={headerRef}
          className={`fixed top-0 left-0 w-full flex justify-center z-[100] transition-all duration-500 ${hasBackground
              ? "bg-black/80 backdrop-blur-md"
              : "bg-transparent"
            }`}
        >
          <div
            style={{ gridTemplateColumns: "1fr auto" }}
            className="w-[1150px] min-h-20 py-4 px-6 grid items-center text-white/80 duration-500"
          >
            <img src={getCurrentLogo()} className="h-10 circle-mask" alt="Logo" />
            <div
              className="flex gap-x-10 items-center text-sm tracking-[0.12em] font-medium"
            >
              {constants.NAVIGATION.map((item) => {
                if (!item.children) {
                  return (
                    <Link
                      key={item.title}
                      to={item.to}
                      className="relative py-2 text-[13px] tracking-[0.12em] uppercase text-white/80 transition-all duration-300 hover:text-white after:absolute after:left-0 after:-bottom-1 after:h-[1px] after:w-0 after:bg-white/70 after:transition-all after:duration-300 hover:after:w-full"
                    >
                      {item.title}
                    </Link>
                  );
                }

                return (
                  <div
                    key={item.title}
                    className="relative inline-flex items-center cursor-pointer py-2 text-[13px] tracking-[0.12em] uppercase text-white/80 transition-all duration-300 hover:text-white"
                    onMouseEnter={() => setActiveMenu(item.slug)}
                    onMouseLeave={() => setActiveMenu(null)}
                  >
                    <span className="inline-flex items-center">
                      {item.title}

                      <FontAwesomeIcon
                        icon={faAngleDown}
                        className={`ml-2 text-xs transition-transform duration-300 ${activeMenu === item.slug ? "rotate-180" : ""}`}
                      />
                    </span>

                    <AnimatePresence>
                      {activeMenu === item.slug && (
                        <motion.div
                          initial={{
                            opacity: 0,
                            x: "-50%",
                            y: -25,
                          }}
                          animate={{
                            opacity: 1,
                            x: "-50%",
                            y: 0,
                          }}
                          exit={{
                            opacity: 0,
                            x: "-50%",
                            y: -15,
                          }}
                          transition={{
                            duration: 0.35,
                          }}
                          className="absolute left-1/2 top-full -translate-x-1/2 mt-4 w-[230px] overflow-hidden rounded-md bg-[#222] border border-white/10 shadow-xl z-20 text-white"
                        >
                          {item.children.map((child) => (
                            <Link
                              key={child.to}
                              to={child.to}
                              className="block px-5 py-3 text-center text-sm tracking-wide text-white/70 transition-all duration-300 hover:bg-white/5 hover:text-white"
                            >
                              {child.title}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <MobileHeader location={location} path={path} adjustHeader={adjustHeader} headerRef={headerRef} />
      )}
    </>
  );
}
